import React from 'react';
import classNames from 'classnames';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import ClipsPlayPauseButton from 'components/ClipsPlayPauseButton/ClipsPlayPauseButton';
import NextWave from 'components/Icons/NextWave';
import PrevWave from 'components/Icons/PrevWave';
import LiveCamButton from 'components/LiveCamButton/LiveCamButton';

import { SpotDetails } from 'types/spot';

import styles from './ReelPlayerCamControls.module.scss';

interface ReelPlayerCamControlsProps {
  isDisabled?: boolean;
  isFirstReel: boolean;
  isLastReel: boolean;
  adRunning: boolean;
  playerPlaying: boolean;
  spot: SpotDetails;
  onPrevious: () => void;
  onNext: () => void;
  onPlayPauseButtonClick: () => void;
}

interface CamControlsContentProps {
  isFirstReel: boolean;
  adRunning: boolean;
  isDisabled: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onPlayPauseButtonClick: () => void;
  playerPlaying: boolean;
  isLastReel: boolean;
}

const CamControlsContent = ({
  isFirstReel,
  adRunning,
  isDisabled,
  onPrevious,
  onNext,
  onPlayPauseButtonClick,
  playerPlaying,
  isLastReel,
}: CamControlsContentProps) => {
  return (
    <Box className={styles.controlsContainer}>
      <Box className={styles.controls}>
        <button
          type="button"
          data-testid="prev-wave-button"
          aria-disabled={isFirstReel || adRunning}
          disabled={isFirstReel || adRunning || isDisabled}
          className={classNames({
            [styles.controlButton]: true,
            [styles.controlButtonDisabled]: isFirstReel || adRunning || isDisabled,
          })}
          onClick={onPrevious}
        >
          <PrevWave />
          <Typography variant="callout2" className={styles.skipWave}>
            Prev. Clip
          </Typography>
        </button>
        <ClipsPlayPauseButton
          isPlaying={playerPlaying}
          onClick={onPlayPauseButtonClick}
          disableRewindPlayButton={adRunning || isDisabled}
          bgFill="white"
        />
        <button
          type="button"
          data-testid="next-highlight-reel-button"
          disabled={isLastReel || adRunning || isDisabled}
          aria-disabled={isLastReel || adRunning || isDisabled}
          className={classNames({
            [styles.controlButton]: true,
            [styles.controlButtonDisabled]: isLastReel || adRunning || isDisabled,
          })}
          onClick={onNext}
        >
          <NextWave />
          <Typography variant="callout2" className={styles.skipWave}>
            Next Clip
          </Typography>
        </button>
      </Box>
    </Box>
  );
};

const ReelPlayerCamControls = ({
  isDisabled = false,
  isFirstReel,
  isLastReel,
  adRunning,
  playerPlaying,
  spot,
  onPrevious,
  onNext,
  onPlayPauseButtonClick,
}: ReelPlayerCamControlsProps) => {
  return (
    <div className={styles.playerControls}>
      <div className={styles.camRewindsControlBarMobile}>
        <Box className={styles.liveCamButtonContainerMobile}>
          <LiveCamButton
            className={styles.liveCamButton}
            dataTestId="controls-go-live-button"
            spot={spot}
            size="small"
          />
        </Box>
      </div>
      <div className={styles.camRewindsControlBarDesktop}>
        <CamControlsContent
          isFirstReel={isFirstReel}
          adRunning={adRunning}
          isDisabled={isDisabled}
          onPrevious={onPrevious}
          onNext={onNext}
          onPlayPauseButtonClick={onPlayPauseButtonClick}
          playerPlaying={playerPlaying}
          isLastReel={isLastReel}
        />
        <Box className={styles.liveCamButtonContainer}>
          <LiveCamButton
            className={styles.liveCamButton}
            dataTestId="controls-go-live-button"
            spot={spot}
          />
        </Box>
      </div>
    </div>
  );
};

ReelPlayerCamControls.displayName = 'ReelPlayerCamControls';

export default React.memo(ReelPlayerCamControls);
